import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mic, 
  MicOff, 
  RotateCcw, 
  ChevronLeft, 
  ChevronRight,
  Volume2,
  AlertCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEchoReaderAudio } from '../hooks/useEchoReaderAudio';
import { useEchoReaderAnalysis } from '../hooks/useEchoReaderAnalysis';
import Button from './ui/Button';
import LoadingSpinner from './ui/LoadingSpinner';

export interface Story {
  id: string;
  title: string;
  content: string;
  sentences: string[];
  difficulty: string;
  storyType: 'echo_reader';
}

interface EchoReaderProps {
  story: Story;
  onComplete?: () => void;
  onBack?: () => void;
  className?: string;
}

const EchoReader: React.FC<EchoReaderProps> = ({
  story,
  onComplete,
  onBack,
  className = ''
}) => {
  const { t } = useTranslation();
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [selectedWordIndex, setSelectedWordIndex] = useState<number | null>(null);

  // Audio recording hook
  const {
    isRecording,
    currentRecording,
    error: audioError,
    isInitialized,
    initializeAudio,
    startRecording,
    stopRecording,
    getRecording,
    deleteRecording,
  } = useEchoReaderAudio();

  // Speech analysis hook
  const {
    isAnalyzing,
    error: analysisError,
    analyzeSpeech,
    getAnalysisResult,
    getWordStatus,
    getSentenceScore,
    getFeedbackMessage,
    getWordSuggestions,
    getStoryProgress,
  } = useEchoReaderAnalysis();

  const currentSentence = story.sentences[currentSentenceIndex];
  const words = currentSentence?.split(' ') || [];
  const analysisResult = getAnalysisResult(currentSentenceIndex);
  const sentenceScore = getSentenceScore(currentSentenceIndex);
  const feedbackMessage = getFeedbackMessage(currentSentenceIndex);

  // Initialize audio on component mount
  useEffect(() => {
    if (!isInitialized) {
      initializeAudio();
    }
  }, [isInitialized, initializeAudio]);

  // Handle recording completion
  useEffect(() => {
    if (currentRecording && currentRecording.sentenceIndex === currentSentenceIndex) {
      // Automatically analyze the recording
      analyzeSpeech(
        story.id,
        currentSentenceIndex,
        currentSentence,
        currentRecording.blob
      ).then(() => {
        setShowFeedback(true);
      });
    }
  }, [currentRecording, currentSentenceIndex, story.id, currentSentence, analyzeSpeech]);

  // Handle recording start
  const handleStartRecording = useCallback(async () => {
    if (!isInitialized) {
      await initializeAudio();
    }
    
    setShowFeedback(false);
    setSelectedWordIndex(null);
    await startRecording(currentSentenceIndex);
  }, [isInitialized, initializeAudio, startRecording, currentSentenceIndex]);

  // Handle recording stop
  const handleStopRecording = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  // Handle recording retry
  const handleRetryRecording = useCallback(() => {
    deleteRecording(currentSentenceIndex);
    setShowFeedback(false);
    setSelectedWordIndex(null);
  }, [deleteRecording, currentSentenceIndex]);

  // Navigate to next sentence
  const handleNextSentence = useCallback(() => {
    if (currentSentenceIndex < story.sentences.length - 1) {
      setCurrentSentenceIndex(prev => prev + 1);
      setShowFeedback(false);
      setSelectedWordIndex(null);
    } else {
      // Story completed
      if (onComplete) {
        onComplete();
      }
    }
  }, [currentSentenceIndex, story.sentences.length, onComplete]);

  // Navigate to previous sentence
  const handlePreviousSentence = useCallback(() => {
    if (currentSentenceIndex > 0) {
      setCurrentSentenceIndex(prev => prev - 1);
      setShowFeedback(false);
      setSelectedWordIndex(null);
    }
  }, [currentSentenceIndex]);

  // Handle word click for practice
  const handleWordClick = useCallback((wordIndex: number) => {
    if (!analysisResult) return;
    
    setSelectedWordIndex(wordIndex);
    // You could add individual word practice here
  }, [analysisResult]);

  // Play sentence audio (text-to-speech)
  const handlePlaySentence = useCallback(() => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(currentSentence);
      utterance.rate = 0.8; // Slower for learning
      utterance.pitch = 1.0;
      speechSynthesis.speak(utterance);
    }
  }, [currentSentence]);

  // Get word color based on analysis status
  const getWordColor = useCallback((wordIndex: number) => {
    const status = getWordStatus(currentSentenceIndex, wordIndex);
    switch (status) {
      case 'correct':
        return 'text-green-600 bg-green-100';
      case 'close':
        return 'text-yellow-600 bg-yellow-100';
      case 'incorrect':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-700 bg-gray-100';
    }
  }, [getWordStatus, currentSentenceIndex]);

  // Calculate progress
  const progress = getStoryProgress(story.sentences.length);

  return (
    <div className={`max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          {onBack && (
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="flex items-center space-x-2"
            >
              <ChevronLeft size={16} />
              <span>{t('common.back', 'Back')}</span>
            </Button>
          )}
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{story.title}</h2>
            <p className="text-sm text-gray-600">
              {t('echoReader.sentence', 'Sentence')} {currentSentenceIndex + 1} {t('common.of', 'of')} {story.sentences.length}
            </p>
          </div>
        </div>
        
        {/* Progress */}
        <div className="text-right">
          <div className="text-sm text-gray-600">
            {t('echoReader.progress', 'Progress')}: {progress.completed}/{story.sentences.length}
          </div>
          {progress.averageScore > 0 && (
            <div className="text-sm font-semibold text-blue-600">
              {t('echoReader.avgScore', 'Avg Score')}: {progress.averageScore.toFixed(1)}/10
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        {/* Sentence Display */}
        <div className="bg-blue-50 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-blue-800">
              {t('echoReader.readAloud', 'Read this sentence aloud:')}
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePlaySentence}
              className="flex items-center space-x-2"
            >
              <Volume2 size={16} />
              <span>{t('echoReader.listen', 'Listen')}</span>
            </Button>
          </div>
          
          <div className="text-xl leading-relaxed font-medium">
            {words.map((word, index) => (
              <span
                key={index}
                className={`inline-block px-2 py-1 m-1 rounded cursor-pointer transition-all duration-200 ${
                  showFeedback ? getWordColor(index) : 'text-gray-700 hover:bg-gray-100'
                } ${selectedWordIndex === index ? 'ring-2 ring-blue-400' : ''}`}
                onClick={() => handleWordClick(index)}
              >
                {word}
              </span>
            ))}
          </div>
        </div>

        {/* Recording Controls */}
        <div className="flex justify-center space-x-4">
          <AnimatePresence>
            {!isRecording && !currentRecording && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
              >
                <Button
                  onClick={handleStartRecording}
                  disabled={!isInitialized || isAnalyzing}
                  className="flex items-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-8 py-3 text-lg"
                >
                  <Mic size={24} />
                  <span>{t('echoReader.startRecording', 'Start Recording')}</span>
                </Button>
              </motion.div>
            )}

            {isRecording && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="flex items-center space-x-4"
              >
                <Button
                  onClick={handleStopRecording}
                  className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-8 py-3 text-lg animate-pulse"
                >
                  <MicOff size={24} />
                  <span>{t('echoReader.stopRecording', 'Stop Recording')}</span>
                </Button>
                
                <div className="flex items-center space-x-2 text-red-600">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="font-medium">{t('echoReader.recording', 'Recording...')}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Analysis Progress */}
        {isAnalyzing && (
          <div className="flex justify-center items-center space-x-3 py-4">
            <LoadingSpinner size="sm" />
            <span className="text-gray-600">{t('echoReader.analyzing', 'Analyzing your pronunciation...')}</span>
          </div>
        )}

        {/* Feedback Display */}
        <AnimatePresence>
          {showFeedback && analysisResult && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-gray-50 rounded-lg p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold text-gray-800">
                  {t('echoReader.feedback', 'Feedback')}
                </h4>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${
                      sentenceScore && sentenceScore >= 8 ? 'bg-green-500' : 
                      sentenceScore && sentenceScore >= 6 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}></div>
                    <span className="font-semibold">
                      {sentenceScore?.toFixed(1)}/10
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRetryRecording}
                    className="flex items-center space-x-2"
                  >
                    <RotateCcw size={16} />
                    <span>{t('echoReader.retry', 'Try Again')}</span>
                  </Button>
                </div>
              </div>

              {feedbackMessage && (
                <div className="mb-4 p-4 bg-blue-100 rounded-lg">
                  <p className="text-blue-800">{feedbackMessage}</p>
                </div>
              )}

              {selectedWordIndex !== null && (
                <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                  <h5 className="font-semibold text-yellow-800 mb-2">
                    {t('echoReader.wordPractice', 'Word Practice')}:&nbsp;
                    <span className="font-normal">"{words[selectedWordIndex]}"</span>
                  </h5>
                  {getWordSuggestions(currentSentenceIndex, selectedWordIndex) && (
                    <p className="text-yellow-700">
                      {getWordSuggestions(currentSentenceIndex, selectedWordIndex)}
                    </p>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between items-center pt-6">
          <Button
            variant="outline"
            onClick={handlePreviousSentence}
            disabled={currentSentenceIndex === 0}
            className="flex items-center space-x-2"
          >
            <ChevronLeft size={16} />
            <span>{t('echoReader.previous', 'Previous')}</span>
          </Button>

          <div className="flex items-center space-x-2 text-sm text-gray-600">
            {Array.from({ length: story.sentences.length }, (_, i) => (
              <div
                key={i}
                className={`w-3 h-3 rounded-full ${
                  i === currentSentenceIndex
                    ? 'bg-blue-500'
                    : getRecording(i)
                    ? getSentenceScore(i) && getSentenceScore(i)! >= 7
                      ? 'bg-green-400'
                      : 'bg-yellow-400'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          <Button
            onClick={handleNextSentence}
            disabled={!analysisResult && currentSentenceIndex < story.sentences.length - 1}
            className="flex items-center space-x-2"
          >
            <span>
              {currentSentenceIndex === story.sentences.length - 1
                ? t('echoReader.finish', 'Finish')
                : t('echoReader.next', 'Next')}
            </span>
            <ChevronRight size={16} />
          </Button>
        </div>

        {/* Error Display */}
        {(audioError || analysisError) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2 text-red-800">
              <AlertCircle size={16} />
              <span className="font-medium">{t('common.error', 'Error')}</span>
            </div>
            <p className="text-red-700 mt-1">{audioError || analysisError}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EchoReader;